if (!window.__budgiverseHeroScriptLoaded) {
  window.__budgiverseHeroScriptLoaded = true;

  const sectionCleanupMap = new WeakMap();
  let resizeTimeout = null;

  function debounceReinit() {
    window.clearTimeout(resizeTimeout);
    resizeTimeout = window.setTimeout(initBudgiverseHeroSections, 220);
  }

  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function isLiteMode() {
    return (
      prefersReducedMotion()
      || window.matchMedia('(max-width: 749px)').matches
      || window.matchMedia('(pointer: coarse)').matches
    );
  }

  function shouldEnhanceText() {
    return !isLiteMode() && window.matchMedia('(pointer: fine)').matches;
  }

  function buildTextSpans(el, text, wordClassName, letterClassName) {
    if (!el) return;

    el.textContent = '';
    text.split(' ').forEach((word, wordIndex, words) => {
      const wordSpan = document.createElement('span');
      wordSpan.className = wordClassName;

      word.split('').forEach((letter) => {
        const letterSpan = document.createElement('span');
        letterSpan.className = letterClassName;
        letterSpan.textContent = letter;
        wordSpan.appendChild(letterSpan);
      });

      el.appendChild(wordSpan);

      if (wordIndex < words.length - 1) {
        el.appendChild(document.createTextNode(' '));
      }
    });
  }

  function updateHeroCopy(el, text, enhanceText, wordClassName, letterClassName) {
    if (!el) return;

    if (enhanceText) {
      buildTextSpans(el, text, wordClassName, letterClassName);
      return;
    }

    el.textContent = text;
  }

  function cleanupSection(sectionEl) {
    const cleanup = sectionCleanupMap.get(sectionEl);
    if (typeof cleanup === 'function') cleanup();
    sectionCleanupMap.delete(sectionEl);
  }

  function initHeroAnalytics(sectionEl) {
    const cta = sectionEl.querySelector('.portal-cta');
    if (!cta) return function noop() {};

    const handleClick = () => {
      const label = cta.dataset.analyticsLabel || cta.textContent.trim();
      window.budgiversePublishAnalyticsEvent?.('hero_cta_clicked', { label });
    };

    cta.addEventListener('click', handleClick, { passive: true });

    return function cleanupHeroAnalytics() {
      cta.removeEventListener('click', handleClick);
    };
  }

  function initGsapEffects(sectionEl, enhanceText) {
    const hasGsap = typeof gsap !== 'undefined' && typeof ScrollTrigger !== 'undefined';
    if (!hasGsap || isLiteMode()) return function noop() {};

    if (!window.__budgiverseHeroGsapRegistered) {
      gsap.registerPlugin(ScrollTrigger);
      window.__budgiverseHeroGsapRegistered = true;
    }

    const cleanups = [];
    const title = sectionEl.querySelector('.surreal-title');
    const originalText = title ? (title.dataset.text || title.textContent.trim()) : '';
    const altText = title ? (title.dataset.altText || '') : '';
    const glitchText = sectionEl.querySelector('.title-glitch text');
    const overlay = sectionEl.querySelector('.glitch-overlay');
    const cta = sectionEl.querySelector('.portal-cta');
    const memoryPulse = sectionEl.querySelector('.memory-pulse');

    function swapTitleText(text) {
      if (!title) return;

      gsap.to(title, {
        opacity: 0,
        duration: 0.22,
        ease: 'power1.in',
        onComplete() {
          updateHeroCopy(title, text, enhanceText, 'surreal-word', 'surreal-letter');
          if (glitchText) glitchText.textContent = text;
          gsap.to(title, { opacity: 1, duration: 0.35, ease: 'power1.out' });
        },
      });
    }

    if (title && glitchText && altText && originalText) {
      const titleTrigger = ScrollTrigger.create({
        trigger: sectionEl,
        start: 'top 25%',
        end: 'top 10%',
        onEnter() {
          swapTitleText(altText);
        },
        onLeaveBack() {
          swapTitleText(originalText);
        },
      });

      cleanups.push(() => titleTrigger.kill());
    }

    if (overlay) {
      const overlayTween = gsap.to(overlay, {
        y: -40,
        ease: 'none',
        scrollTrigger: {
          trigger: sectionEl,
          start: 'top bottom',
          end: 'bottom top',
          scrub: true,
        },
      });

      cleanups.push(() => {
        if (overlayTween.scrollTrigger) overlayTween.scrollTrigger.kill();
        overlayTween.kill();
      });
    }

    if (cta && memoryPulse) {
      const handleClick = () => {
        gsap.killTweensOf(memoryPulse);
        gsap.fromTo(
          memoryPulse,
          { opacity: 0 },
          { opacity: 1, duration: 0.25, yoyo: true, repeat: 1, ease: 'power1.inOut' }
        );
      };

      cta.addEventListener('click', handleClick);
      cleanups.push(() => cta.removeEventListener('click', handleClick));
    }

    return function cleanupGsapEffects() {
      cleanups.forEach((cleanup) => cleanup());
    };
  }

  function initParticleField(sectionEl) {
    const canvas = sectionEl.querySelector('.particle-field');
    if (!canvas) return function noop() {};

    const liteMode = isLiteMode();
    sectionEl.dataset.heroLite = liteMode ? 'true' : 'false';

    if (liteMode) {
      canvas.hidden = true;
      return function noop() {};
    }

    canvas.hidden = false;

    const ctx = canvas.getContext('2d');
    if (!ctx) return function noop() {};

    const particles = [];
    let animationFrameId = null;
    let isVisible = false;
    const allowConnections = window.matchMedia('(min-width: 990px) and (pointer: fine)').matches;
    const dpr = Math.min(window.devicePixelRatio || 1, 1.5);
    let canvasW = 1;
    let canvasH = 1;

    function resizeCanvas() {
      const width = Math.max(sectionEl.offsetWidth, 1);
      const height = Math.max(sectionEl.offsetHeight, 1);
      canvasW = width;
      canvasH = height;

      canvas.width = Math.floor(width * dpr);
      canvas.height = Math.floor(height * dpr);
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    }

    function resetParticle(particle, color1, color2) {
      particle.x = Math.random() * canvasW;
      particle.y = Math.random() * canvasH;
      particle.size = Math.random() * 1.5 + 0.5;
      particle.speedX = (Math.random() - 0.5) * 0.24;
      particle.speedY = (Math.random() - 0.5) * 0.24;
      particle.opacity = Math.random() * 0.35 + 0.18;
      particle.color = Math.random() > 0.5 ? color1 : color2;
    }

    function createParticles() {
      const styles = getComputedStyle(sectionEl);
      const color1 = styles.getPropertyValue('--glitch-c1').trim() || '#00fff7';
      const color2 = styles.getPropertyValue('--glitch-c2').trim() || '#ff00cc';
      const particleCount = Math.min(
        Math.floor((canvasW * canvasH) / 12000),
        60
      );

      particles.length = 0;
      for (let i = 0; i < particleCount; i += 1) {
        const particle = {};
        resetParticle(particle, color1, color2);
        particles.push(particle);
      }
    }

    function drawConnections() {
      if (!allowConnections) return;

      const maxDistance = 110;

      for (let i = 0; i < particles.length; i += 1) {
        for (let j = i + 1; j < particles.length; j += 1) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const distance = Math.sqrt(dx * dx + dy * dy);

          if (distance >= maxDistance) continue;

          ctx.strokeStyle = particles[i].color;
          ctx.globalAlpha = (1 - distance / maxDistance) * 0.15;
          ctx.lineWidth = 0.5;
          ctx.beginPath();
          ctx.moveTo(particles[i].x, particles[i].y);
          ctx.lineTo(particles[j].x, particles[j].y);
          ctx.stroke();
        }
      }
    }

    function animate() {
      if (!isVisible) return;

      ctx.clearRect(0, 0, canvasW, canvasH);

      particles.forEach((particle) => {
        particle.x += particle.speedX;
        particle.y += particle.speedY;

        if (particle.x < 0) particle.x = canvasW;
        if (particle.x > canvasW) particle.x = 0;
        if (particle.y < 0) particle.y = canvasH;
        if (particle.y > canvasH) particle.y = 0;

        particle.opacity += (Math.random() - 0.5) * 0.015;
        particle.opacity = Math.max(0.08, Math.min(0.55, particle.opacity));

        ctx.fillStyle = particle.color;
        ctx.globalAlpha = particle.opacity;
        ctx.beginPath();
        ctx.arc(particle.x, particle.y, particle.size, 0, Math.PI * 2);
        ctx.fill();
      });

      drawConnections();
      animationFrameId = window.requestAnimationFrame(animate);
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        isVisible = !!entry && entry.isIntersecting;

        if (isVisible && !animationFrameId) {
          animate();
        } else if (!isVisible && animationFrameId) {
          window.cancelAnimationFrame(animationFrameId);
          animationFrameId = null;
        }
      },
      { threshold: 0.08, rootMargin: '120px 0px' }
    );

    const handleResize = () => {
      resizeCanvas();
      createParticles();
    };

    resizeCanvas();
    createParticles();
    observer.observe(sectionEl);
    window.addEventListener('resize', handleResize, { passive: true });

    return function cleanupParticleField() {
      observer.disconnect();
      window.removeEventListener('resize', handleResize);
      if (animationFrameId) window.cancelAnimationFrame(animationFrameId);
      ctx.clearRect(0, 0, canvasW, canvasH);
    };
  }

  function initSectionStateObserver(sectionEl) {
    if (isLiteMode()) {
      sectionEl.classList.remove('scrolled');
      return function noop() {};
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry) return;

        sectionEl.classList.toggle(
          'scrolled',
          entry.boundingClientRect.top < 0 && entry.intersectionRatio < 0.7
        );
      },
      { threshold: [0, 0.7, 1] }
    );

    observer.observe(sectionEl);

    return function cleanupSectionStateObserver() {
      observer.disconnect();
    };
  }

  function initBudgiverseHeroSections() {
    const enhanceText = shouldEnhanceText();

    document.querySelectorAll('section.budgiverse-glitch-hero').forEach((sectionEl) => {
      cleanupSection(sectionEl);

      const cleanups = [];
      const title = sectionEl.querySelector('.surreal-title');
      const subtext = sectionEl.querySelector('.budgi-subtext');
      const titleText = title ? (title.dataset.text || title.textContent.trim()) : '';
      const subtextText = subtext ? (subtext.dataset.text || subtext.textContent.trim()) : '';

      sectionEl.dataset.heroEnhanced = enhanceText ? 'true' : 'false';
      sectionEl.dataset.heroLite = isLiteMode() ? 'true' : 'false';

      updateHeroCopy(title, titleText, enhanceText, 'surreal-word', 'surreal-letter');
      updateHeroCopy(subtext, subtextText, enhanceText, 'subtext-word', 'subtext-letter');

      cleanups.push(initHeroAnalytics(sectionEl));
      cleanups.push(initGsapEffects(sectionEl, enhanceText));
      cleanups.push(initParticleField(sectionEl));
      cleanups.push(initSectionStateObserver(sectionEl));

      sectionCleanupMap.set(sectionEl, function cleanupAll() {
        cleanups.forEach((cleanup) => {
          if (typeof cleanup === 'function') cleanup();
        });
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initBudgiverseHeroSections, { once: true });
  } else {
    initBudgiverseHeroSections();
  }

  document.addEventListener('budgiverse:gsap:ready', initBudgiverseHeroSections);
  document.addEventListener('shopify:section:load', initBudgiverseHeroSections);
  document.addEventListener('shopify:section:unload', (event) => {
    const sectionEl = event?.target?.querySelector?.('.budgiverse-glitch-hero') || event?.target;
    if (sectionEl) cleanupSection(sectionEl);
  });
  window.addEventListener('resize', debounceReinit, { passive: true });
}
